/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DateTime } from "@tivocorp/data-domain-lib";
import type {
  TChannel,
  TGridGuideRow,
  TGuideCell
} from "@tivocorp/data-domain-lib";

import { ChannelsInteractor } from "../../model/interactor";
import { Log, DiagnosticLogger, OperationalLogger } from "../../model/logging";
import { DiagnosticSeverity } from "../../model/types";
import {
  GUIDE_WINDOW_DURATION_MINUTES,
  GUIDE_MAX_CACHED_TIME_MINUTES,
  GUIDE_TOTAL_CACHED_CHANNELS,
  GUIDE_MAX_FETCH_WINDOW_MINUTES,
  GUIDE_MAX_STATIONS_PER_REQ,
  GUIDE_PREFETCH_TIME_CHUNK_MINUTES,
  GUIDE_TIME_BUFFER_SLOTS,
  MILLISECONDS_IN_A_SECOND,
  MILLISECONDS_PER_MINUTE,
  GUIDE_SHIFT_MS
} from "../../config";
import {
  GUIDE_ERROR_CODE,
  GUIDE_SCREEN_NAME,
  GuideErrorMapper,
  isCancellation
} from "./guideErrorMapper";

/** A contiguous, half-open time range [startMs, endMs) that has been fetched. */
export interface GuideFetchedRange {
  startMs: number;
  endMs: number;
}

/**
 * Duration (seconds) of the synthetic guide cell used for Jump Channels.
 * Jump Channels have no linear schedule, so the guide displays a single
 * cell that spans the entire visible time frame and remains identical when
 * shifting into the past or future. 1000 years is large enough to cover any
 * navigable guide window while staying well within safe-integer range.
 */
const JUMP_CHANNEL_CELL_DURATION_SECONDS: number = 1000 * 365 * 24 * 60 * 60;

/**
 * Per-station program data held in the in-memory guide cache.
 *
 * Programs are kept sorted by start time and de-duplicated; fetchedRanges
 * records which time spans have been retrieved so the cache can answer
 * "is this window already covered?" without re-fetching.
 */
export interface GuideRowData {
  stationId: string;
  programs: TGuideCell[];
  /** Ranges the backend answered with at least one real program. */
  fetchedRanges: GuideFetchedRange[];
  /**
   * Ranges the backend answered for this station with zero programs.
   *
   * Kept separate from `fetchedRanges` so availability never masquerades as
   * content: an empty range suppresses re-requesting (no fetch storm) but
   * contributes no cells, so the row renders as a loading placeholder and
   * a real program arriving later can never be hidden by it.
   */
  emptyRanges: GuideFetchedRange[];
  earliestMs: number;
  latestMs: number;
}

/** One channel slot in the circular in-memory channel window. */
export interface GuideChannelEntry {
  globalIndex: number;
  stationId: string;
  channel: TChannel;
}

/**
 * In-memory sliding-window cache for the Guide (EPG) screen.
 *
 * Holds a fixed window of channels (TOTAL_CACHED_CHANNELS) plus per-station
 * program data keyed by stationId. Channel metadata itself remains persisted
 * in IndexedDB (CHANNELS); this cache only keeps the active window in
 * memory and de-duplicates program cells across overlapping fetches.
 *
 * Use {@link GuideCache.getInstance} to obtain the shared instance.
 */
export class GuideCache {
  private static readonly TAG: string = "GuideCache";

  private static mInstance: GuideCache | null = null;

  /** Per-station program data keyed by stationId. */
  private readonly mRows: Map<string, GuideRowData> = new Map();

  /**
   * Jump Channel stations discovered so far, keyed by stationId. Populated
   * whenever a channel list flows through the cache (setChannelWindow, fetch,
   * fetchRange, etc). These stations never hit the network — they are always
   * served via a single synthetic full-span guide cell.
   */
  private readonly mJumpStationChannels: Map<string, TChannel> = new Map();

  /** Per-station synthetic full-span guide cell for Jump Channels, cached once built. */
  private readonly mJumpChannelCells: Map<string, TGuideCell> = new Map();

  /** Circular window of cached channels (≤ TOTAL_CACHED_CHANNELS). */
  private mChannelWindow: GuideChannelEntry[] = [];

  /** Total number of channels across the full channel list. 0 until set. */
  private mTotalChannelCount: number = 0;

  /** In-flight guide-rows requests, keyed by requestKey, for de-duplication. */
  private readonly mInFlight: Map<string, Promise<TGridGuideRow[]>> = new Map();

  /**
   * AbortController for the current batch of in-flight fetchChunk calls.
   * Replaced on every abortAndReset() invocation so that a new window load
   * can cancel its predecessor's network requests. [G7]
   */
  private mFetchAbortController: AbortController = new AbortController();

  private constructor() {}

  public static getInstance(): GuideCache {
    if (GuideCache.mInstance === null) {
      GuideCache.mInstance = new GuideCache();
    }
    return GuideCache.mInstance;
  }

  /**
   * Aligns an epoch-millisecond value down to the nearest 30-minute boundary.
   * All cache/request boundaries are 30-minute aligned.
   *
   * @param epochMs Epoch milliseconds to align.
   * @returns The aligned epoch milliseconds.
   */
  public static alignTo30Min(epochMs: number): number {
    return epochMs - (epochMs % GUIDE_SHIFT_MS);
  }

  /**
   * Builds a stable request key from a set of stations and a time range.
   * Station order is normalised so equivalent requests collapse to one key.
   *
   * @param stationIds Stations included in the request.
   * @param startMs Window start in epoch milliseconds.
   * @param endMs Window end in epoch milliseconds.
   * @returns A deterministic request key.
   */
  public static requestKey(
    stationIds: string[],
    startMs: number,
    endMs: number
  ): string {
    const sorted: string[] = [...stationIds].sort((a, b) => a.localeCompare(b));
    return sorted.join(",") + "|" + startMs + "|" + endMs;
  }

  /**
   * Returns the cache key for a channel. Prefers stationId; falls back to
   * channelId for channels without a station (Jump Channels have no linear
   * station and may omit stationId entirely).
   *
   * @param channel Channel to derive the key from.
   * @returns The cache key, or an empty string when neither id is present.
   */
  public static channelKey(channel: TChannel): string {
    if (channel.stationId !== undefined && channel.stationId !== null) {
      return channel.stationId;
    }
    if (channel.channelId !== undefined && channel.channelId !== null) {
      return channel.channelId;
    }
    return "";
  }

  /**
   * Records any Jump Channels found in the given list so their stations can
   * later be recognised by {@link isJumpStation}. Additive — never removes a
   * previously-seen station, since callers may pass narrower subsets (e.g. an
   * uncovered-only batch) than the full channel window.
   *
   * @param channels Channels to scan for jumpChannelInfo.
   * @returns Nothing.
   */
  private registerJumpChannels(channels: TChannel[]): void {
    for (const channel of channels) {
      if (channel.jumpChannelInfo === undefined) {
        continue;
      }
      const key: string = GuideCache.channelKey(channel);
      if (key.length === 0) {
        continue;
      }
      this.mJumpStationChannels.set(key, channel);
    }
  }

  /** Returns true when stationId belongs to a known Jump Channel. */
  private isJumpStation(stationId: string): boolean {
    return this.mJumpStationChannels.has(stationId);
  }

  /**
   * Builds (and caches) the synthetic full-span guide cell for a Jump Channel
   * station. The cell starts at epoch 0 and spans a very long duration so it
   * always covers the entire visible window, identically in the past and
   * future. Title prefers jumpChannelInfo.metadata.guideCellTitle, falling
   * back to the channel name.
   *
   * @param stationId Jump Channel station id.
   * @returns The synthetic guide cell for this station.
   */
  private getSyntheticJumpCell(stationId: string): TGuideCell {
    const cached: TGuideCell | undefined =
      this.mJumpChannelCells.get(stationId);
    if (cached !== undefined) {
      return cached;
    }

    const channel: TChannel | undefined =
      this.mJumpStationChannels.get(stationId);
    const metadata =
      channel !== undefined && channel.jumpChannelInfo !== undefined
        ? channel.jumpChannelInfo.metadata
        : undefined;
    const guideCellTitle: string =
      metadata !== undefined &&
      metadata.guideCellTitle !== undefined &&
      metadata.guideCellTitle.length > 0
        ? metadata.guideCellTitle
        : "";
    const channelName: string =
      channel !== undefined && channel.name !== undefined ? channel.name : "";
    const title: string =
      guideCellTitle.length > 0 ? guideCellTitle : channelName;

    const cell: TGuideCell = Object.freeze({
      collectionId: "",
      contentId: "jump-" + stationId,
      duration: JUMP_CHANNEL_CELL_DURATION_SECONDS,
      offerId: "",
      posterImageUrl: "",
      startTime: new DateTime(0),
      title
    });
    this.mJumpChannelCells.set(stationId, cell);
    return cell;
  }

  /**
   * Replaces the cached channel window with the provided ordered channels.
   * Only the first TOTAL_CACHED_CHANNELS entries are retained.
   *
   * @param channels Ordered channels forming the active window.
   * @param startGlobalIndex Global index of the first channel in the window.
   * @param totalChannelCount Total number of channels in the full channel list.
   * @returns Nothing.
   */
  public setChannelWindow(
    channels: TChannel[],
    startGlobalIndex: number,
    totalChannelCount: number
  ): void {
    this.registerJumpChannels(channels);
    this.mTotalChannelCount = totalChannelCount;
    const window: GuideChannelEntry[] = [];
    const count: number = Math.min(
      channels.length,
      GUIDE_TOTAL_CACHED_CHANNELS
    );

    for (let index: number = 0; index < count; index++) {
      const channel: TChannel = channels[index];
      const stationId: string = GuideCache.channelKey(channel);
      window.push({
        globalIndex: startGlobalIndex + index,
        stationId,
        channel
      });
    }

    this.mChannelWindow = window;
  }

  /**
   * Maps an arbitrary integer index into the valid circular range
   * [0, total). Safe for negative values.
   *
   * @param index Raw (possibly negative) index.
   * @param total Total count; must be > 0.
   * @returns Index in [0, total).
   */
  private static circularIndex(index: number, total: number): number {
    return ((index % total) + total) % total;
  }

  /** Returns the cached channel window entries (defensive copy). */
  public getChannelWindow(): GuideChannelEntry[] {
    return [...this.mChannelWindow];
  }

  /** Returns the stationIds of every channel currently in the window. */
  public getCachedStationIds(): string[] {
    const ids: string[] = [];
    for (const entry of this.mChannelWindow) {
      ids.push(entry.stationId);
    }
    return ids;
  }

  /**
   * Returns the global index of the first channel in the cached window,
   * or -1 when the window is empty.
   */
  public getWindowStartGlobalIndex(): number {
    if (this.mChannelWindow.length === 0) {
      return -1;
    }
    return this.mChannelWindow[0].globalIndex;
  }

  /** Returns whether the given station is part of the current channel window. */
  public hasStationInWindow(stationId: string): boolean {
    for (const entry of this.mChannelWindow) {
      if (entry.stationId === stationId) {
        return true;
      }
    }
    return false;
  }

  /**
   * Drops per-station program data for any station that is no longer part of
   * the current channel window. Bounds in-memory program data to the active
   * sliding window (GUIDE_TOTAL_CACHED_CHANNELS stations at most).
   *
   * @returns Nothing.
   */
  public pruneProgramsToWindow(): void {
    const windowStationIds: Set<string> = new Set(this.getCachedStationIds());
    this.mRows.forEach((_rowData: GuideRowData, stationId: string): void => {
      if (!windowStationIds.has(stationId)) {
        this.mRows.delete(stationId);
      }
    });
  }

  /**
   * Merges fetched guide rows into the per-station cache for a time range.
   * De-duplicates program cells and records the fetched range so subsequent
   * lookups can be served without a network call.
   *
   * @param rows Guide rows returned by the service.
   * @param startMs Fetched window start in epoch milliseconds.
   * @param endMs Fetched window end in epoch milliseconds.
   * @returns Nothing.
   */
  public mergeRows(
    rows: TGridGuideRow[],
    startMs: number,
    endMs: number
  ): void {
    for (const row of rows) {
      if (row === null || row === undefined) {
        continue;
      }
      this.mergeRow(row, startMs, endMs);
    }
  }

  /**
   * Returns the program cells overlapping [startMs, endMs) for one station.
   *
   * @param stationId Station to read.
   * @param startMs Range start in epoch milliseconds.
   * @param endMs Range end in epoch milliseconds.
   * @returns Overlapping program cells (sorted), or an empty array.
   */
  public getProgramsForRange(
    stationId: string,
    startMs: number,
    endMs: number
  ): TGuideCell[] {
    if (this.isJumpStation(stationId)) {
      return [this.getSyntheticJumpCell(stationId)];
    }

    const rowData: GuideRowData | undefined = this.mRows.get(stationId);
    if (rowData === undefined) {
      return [];
    }

    const result: TGuideCell[] = [];
    for (const cell of rowData.programs) {
      const cellStartMs: number = cell.startTime.getTime();
      const cellEndMs: number =
        cellStartMs + cell.duration * MILLISECONDS_IN_A_SECOND;
      if (cellEndMs > startMs && cellStartMs < endMs) {
        result.push(cell);
      }
    }
    return result;
  }

  /**
   * Returns true when every station fully covers [startMs, endMs) from its
   * recorded fetchedRanges (no network call needed).
   *
   * @param stationIds Stations to check.
   * @param startMs Window start in epoch milliseconds.
   * @param endMs Window end in epoch milliseconds.
   * @returns Whether the window is fully cached for all stations.
   */
  public isWindowCovered(
    stationIds: string[],
    startMs: number,
    endMs: number
  ): boolean {
    for (const id of stationIds) {
      if (!this.isStationCovered(id, startMs, endMs)) {
        return false;
      }
    }
    return stationIds.length > 0;
  }

  /**
   * Evicts cached data so neither the total time span nor the cached channel
   * count exceeds their respective limits (spec §5).
   *
   * Time axis: trims the side farther from the visible centre when the cached
   * duration exceeds GUIDE_MAX_CACHED_TIME_MINUTES.
   *
   * Channel axis: removes stations outside the centred keep range when
   * mRows.size exceeds GUIDE_TOTAL_CACHED_CHANNELS.
   *
   * @param visibleStartMs Start of the visible window, in epoch milliseconds.
   * @returns Nothing.
   */
  public evict(visibleStartMs: number): void {
    // --- Time axis eviction ---
    const extent: GuideFetchedRange | null = this.computeCacheExtent();
    if (extent !== null) {
      const cacheStartTime: number = extent.startMs;
      const cacheEndTime: number = extent.endMs;
      const maxDuration: number =
        GUIDE_MAX_CACHED_TIME_MINUTES * MILLISECONDS_PER_MINUTE;
      const cachedDuration: number = cacheEndTime - cacheStartTime;

      if (cachedDuration > maxDuration) {
        const visibleCenter: number =
          visibleStartMs +
          (GUIDE_WINDOW_DURATION_MINUTES * MILLISECONDS_PER_MINUTE) / 2;
        const distToLeft: number = visibleCenter - cacheStartTime;
        const distToRight: number = cacheEndTime - visibleCenter;

        let newStart: number;
        let newEnd: number;
        if (distToLeft > distToRight) {
          // Trim past (left side)
          newEnd = cacheEndTime;
          newStart = GuideCache.alignTo30Min(cacheEndTime - maxDuration);
        } else {
          // Trim future (right side)
          newStart = cacheStartTime;
          newEnd = GuideCache.alignTo30Min(cacheStartTime + maxDuration);
        }

        this.trimToRange(newStart, newEnd);
      }
    }

    // --- Channel axis eviction ---
    const cachedChannelCount: number = this.mRows.size;
    if (
      cachedChannelCount <= GUIDE_TOTAL_CACHED_CHANNELS ||
      this.mTotalChannelCount === 0 ||
      this.mChannelWindow.length === 0
    ) {
      return;
    }

    const visibleCenter: number =
      this.mChannelWindow[0].globalIndex +
      Math.floor(this.mChannelWindow.length / 2);
    const keepStart: number = GuideCache.circularIndex(
      visibleCenter - Math.floor(GUIDE_TOTAL_CACHED_CHANNELS / 2),
      this.mTotalChannelCount
    );

    const keepIds: Set<string> = new Set<string>();
    for (const entry of this.mChannelWindow) {
      const relativeIndex: number = GuideCache.circularIndex(
        entry.globalIndex - keepStart,
        this.mTotalChannelCount
      );
      if (relativeIndex < GUIDE_TOTAL_CACHED_CHANNELS) {
        keepIds.add(entry.stationId);
      }
    }

    this.mRows.forEach((_rowData: GuideRowData, stationId: string): void => {
      if (!keepIds.has(stationId)) {
        this.mRows.delete(stationId);
      }
    });
  }

  /**
   * Computes the overall cached time extent across every station, derived from
   * recorded fetched ranges.
   *
   * @returns The cached [startMs, endMs) extent, or null when nothing is cached.
   */
  private computeCacheExtent(): GuideFetchedRange | null {
    let startMs: number = Number.POSITIVE_INFINITY;
    let endMs: number = Number.NEGATIVE_INFINITY;

    this.mRows.forEach((rowData: GuideRowData): void => {
      for (
        let index: number = 0;
        index < rowData.fetchedRanges.length;
        index++
      ) {
        const range: GuideFetchedRange = rowData.fetchedRanges[index];
        if (range.startMs < startMs) {
          startMs = range.startMs;
        }
        if (range.endMs > endMs) {
          endMs = range.endMs;
        }
      }
    });

    if (
      startMs === Number.POSITIVE_INFINITY ||
      endMs === Number.NEGATIVE_INFINITY
    ) {
      return null;
    }
    return { startMs, endMs };
  }

  /**
   * Clips every station's program data and fetched ranges to [keepStartMs,
   * keepEndMs), dropping stations left with no data.
   *
   * @param keepStartMs Lower retention boundary, in epoch milliseconds.
   * @param keepEndMs Upper retention boundary, in epoch milliseconds.
   * @returns Nothing.
   */
  private trimToRange(keepStartMs: number, keepEndMs: number): void {
    this.mRows.forEach((rowData: GuideRowData, stationId: string): void => {
      const programs: TGuideCell[] = rowData.programs.filter(
        (cell: TGuideCell): boolean => {
          const cellStartMs: number = cell.startTime.getTime();
          const cellEndMs: number =
            cellStartMs + cell.duration * MILLISECONDS_IN_A_SECOND;
          return cellEndMs > keepStartMs && cellStartMs < keepEndMs;
        }
      );
      const clampToKeepRange = (
        ranges: GuideFetchedRange[]
      ): GuideFetchedRange[] =>
        ranges
          .map((range: GuideFetchedRange): GuideFetchedRange => {
            return {
              startMs: Math.max(range.startMs, keepStartMs),
              endMs: Math.min(range.endMs, keepEndMs)
            };
          })
          .filter(
            (range: GuideFetchedRange): boolean => range.startMs < range.endMs
          );

      const fetchedRanges: GuideFetchedRange[] = clampToKeepRange(
        rowData.fetchedRanges
      );
      const emptyRanges: GuideFetchedRange[] = clampToKeepRange(
        rowData.emptyRanges
      );

      if (
        programs.length === 0 &&
        fetchedRanges.length === 0 &&
        emptyRanges.length === 0
      ) {
        this.mRows.delete(stationId);
        return;
      }

      this.mRows.set(stationId, {
        stationId,
        programs,
        fetchedRanges,
        emptyRanges,
        earliestMs:
          programs.length > 0 ? programs[0].startTime.getTime() : keepStartMs,
        latestMs:
          fetchedRanges.length > 0
            ? fetchedRanges[fetchedRanges.length - 1].endMs
            : keepStartMs
      });
    });
  }

  /**
   * Aborts all in-flight guide fetch requests started by the current
   * AbortController, then creates a fresh one for subsequent requests.
   * Call this before starting a new window load so the previous load's
   * network requests are cancelled and their results discarded.
   * [G7 — in-flight request cancellation]
   *
   * @returns Nothing.
   */
  public abortAndReset(): void {
    this.mFetchAbortController.abort();
    this.mFetchAbortController = new AbortController();
    this.mInFlight.clear();
    Log.d(GuideCache.TAG, "abortAndReset() — cancelled in-flight requests");
    DiagnosticLogger.log(
      "guide_cache",
      "guide cache abort/reset: window_load_superceded",
      DiagnosticSeverity.Info,
      { event: "window_load_superceded" }
    );
  }

  /** Clears all cached channels and program data. */
  public clear(): void {
    this.mRows.clear();
    this.mChannelWindow = [];
    this.mInFlight.clear();
    this.mJumpStationChannels.clear();
    this.mJumpChannelCells.clear();
  }

  /**
   * Stores rows for a standard guide window (start → start + 90min).
   *
   * @param windowStartTime Window start time.
   * @param rows Guide rows to store.
   * @returns Nothing.
   */
  public seed(windowStartTime: DateTime, rows: TGridGuideRow[]): void {
    const startMs: number = windowStartTime.getTime();
    const endMs: number =
      startMs + GUIDE_WINDOW_DURATION_MINUTES * MILLISECONDS_PER_MINUTE;
    this.mergeRows(rows, startMs, endMs);
    this.evict(startMs);
  }

  /**
   * Returns guide rows for the standard window starting at windowStartTime.
   * Serves from cache when the whole window is covered for every channel;
   * otherwise fetches the missing data, merges it, and returns the result.
   * Concurrent identical requests are de-duplicated.
   *
   * @param windowStartTime Window start time.
   * @param channels Channels (in display order) to build rows for.
   * @returns Guide rows aligned with the provided channel order.
   */
  public async fetch(
    windowStartTime: DateTime,
    channels: TChannel[]
  ): Promise<TGridGuideRow[]> {
    this.registerJumpChannels(channels);
    const startMs: number = windowStartTime.getTime();
    const endMs: number =
      startMs + GUIDE_WINDOW_DURATION_MINUTES * MILLISECONDS_PER_MINUTE;
    const stationIds: string[] = channels.map((channel: TChannel): string =>
      GuideCache.channelKey(channel)
    );

    if (this.isWindowCovered(stationIds, startMs, endMs)) {
      Log.d(GuideCache.TAG, "fetch() HIT start=" + startMs);
      DiagnosticLogger.log(
        "guide_cache",
        "guide cache hit",
        DiagnosticSeverity.Debug,
        { outcome: "hit", station_count: String(stationIds.length) }
      );
      return this.buildRows(stationIds, startMs, endMs);
    }

    Log.d(GuideCache.TAG, "fetch() MISS start=" + startMs + " — fetching");
    DiagnosticLogger.log(
      "guide_cache",
      "guide cache miss",
      DiagnosticSeverity.Debug,
      { outcome: "miss", station_count: String(stationIds.length) }
    );
    const key: string = GuideCache.requestKey(stationIds, startMs, endMs);
    const inFlight: Promise<TGridGuideRow[]> | undefined =
      this.mInFlight.get(key);
    if (inFlight !== undefined) {
      return inFlight;
    }

    const request: Promise<TGridGuideRow[]> = this.fetchAndMerge(
      windowStartTime,
      channels,
      startMs,
      endMs,
      key
    );
    this.mInFlight.set(key, request);
    return request;
  }

  /**
   * Classifier-aware fetch for a guide window. (G3 — Gap Fill / Edge Advance)
   *
   * Classifies the load before touching the network:
   *
   *   CACHE_HIT (Edge Advance) — every station in the new window is already
   *   covered in memory (e.g. pre-warmed by a previous prefetchChannelData
   *   call). Rows are built directly from cache with zero network traffic.
   *
   *   GAP_FILL — one or more stations are not yet cached. Only the uncovered
   *   stations are fetched via fetchRange; already-cached stations are served
   *   from memory. This is more efficient than the all-or-nothing fetch()
   *   strategy which re-fetches all 15 stations whenever even one is absent.
   *
   * @param windowStartTime Window start time.
   * @param channels Channels to serve, in display order.
   * @returns Guide rows for the window, ordered by the supplied channel list.
   */
  public async fetchSmart(
    windowStartTime: DateTime,
    channels: TChannel[]
  ): Promise<TGridGuideRow[]> {
    this.registerJumpChannels(channels);
    const startMs: number = windowStartTime.getTime();
    const endMs: number =
      startMs + GUIDE_WINDOW_DURATION_MINUTES * MILLISECONDS_PER_MINUTE;
    const stationIds: string[] = channels.map((channel: TChannel): string =>
      GuideCache.channelKey(channel)
    );

    // Classify: identify stations not yet covered in the cache. Channel lists
    // legitimately contain the same stationId more than once (one station
    // carried on several channel numbers), and the cache is keyed by station,
    // so fetch each distinct station once — otherwise duplicates consume
    // request slots and can be split across batches into redundant requests.
    const uncoveredChannels: TChannel[] = [];
    const requestedStationIds: Set<string> = new Set();
    for (let index: number = 0; index < channels.length; index++) {
      const stationId: string = stationIds[index];
      if (requestedStationIds.has(stationId)) {
        continue;
      }
      if (!this.isStationCovered(stationId, startMs, endMs)) {
        requestedStationIds.add(stationId);
        uncoveredChannels.push(channels[index]);
      }
    }

    if (uncoveredChannels.length === 0) {
      // CACHE_HIT / Edge Advance: all stations already in memory, no network call.
      Log.i(
        GuideCache.TAG,
        "fetchSmart() CACHE_HIT start=" +
          String(startMs) +
          " stations=" +
          String(stationIds.length)
      );
      DiagnosticLogger.log(
        "guide_cache",
        "guide cache cache_hit",
        DiagnosticSeverity.Debug,
        { outcome: "cache_hit", station_count: String(stationIds.length) }
      );
      return this.buildRows(stationIds, startMs, endMs);
    }

    // GAP_FILL: fetch only the uncovered stations.
    Log.i(
      GuideCache.TAG,
      "fetchSmart() GAP_FILL start=" +
        String(startMs) +
        " uncovered=" +
        String(uncoveredChannels.length) +
        "/" +
        String(stationIds.length)
    );
    DiagnosticLogger.log(
      "guide_cache",
      "guide cache gap_fill",
      DiagnosticSeverity.Debug,
      {
        outcome: "gap_fill",
        station_count: String(stationIds.length),
        uncovered_count: String(uncoveredChannels.length)
      }
    );
    await this.fetchRange(windowStartTime, uncoveredChannels, startMs, endMs);
    return this.buildRows(stationIds, startMs, endMs);
  }

  /**
   * Synchronous read of whatever is currently in the cache for a window.
   * No network call is made. Stations not yet cached return an empty
   * `guideCells` array so callers can dispatch a "loading" frame to the UI
   * immediately while waiting for the async gap-fill fetch to complete.
   * [G5 — PARTIAL_HIT immediate serve]
   *
   * @param windowStartTime Window start time.
   * @param channels Channels to read, in display order.
   * @returns Guide rows built from cached data; uncovered stations get `guideCells: []`.
   */
  public getWindowRows(
    windowStartTime: DateTime,
    channels: TChannel[]
  ): TGridGuideRow[] {
    this.registerJumpChannels(channels);
    const startMs: number = windowStartTime.getTime();
    const endMs: number =
      startMs + GUIDE_WINDOW_DURATION_MINUTES * MILLISECONDS_PER_MINUTE;
    const stationIds: string[] = channels.map((channel: TChannel): string =>
      GuideCache.channelKey(channel)
    );
    return this.buildRows(stationIds, startMs, endMs);
  }

  /**
   * Fetches a window from the service, merges it, and returns reconstructed rows.
   *
   * @param windowStartTime Window start time.
   * @param channels Channels to fetch for.
   * @param startMs Window start in epoch milliseconds.
   * @param endMs Window end in epoch milliseconds.
   * @param key Request key used for in-flight de-duplication.
   * @returns Guide rows aligned with the provided channel order.
   */
  private async fetchAndMerge(
    windowStartTime: DateTime,
    channels: TChannel[],
    startMs: number,
    endMs: number,
    key: string
  ): Promise<TGridGuideRow[]> {
    try {
      const interactor: ChannelsInteractor = new ChannelsInteractor();
      // Jump Channels have no linear schedule — never send them to the guide
      // rows service; their cells are synthesized locally instead.
      const fetchableChannels: TChannel[] = channels.filter(
        (channel: TChannel): boolean => channel.jumpChannelInfo === undefined
      );
      let rows: TGridGuideRow[] = [];
      if (fetchableChannels.length > 0) {
        try {
          rows = await interactor.fetchGuideRowsWithFallback({
            channels: fetchableChannels,
            startTime: windowStartTime,
            endTime: windowStartTime.addMinutes(GUIDE_WINDOW_DURATION_MINUTES)
          });
        } catch (error: unknown) {
          GuideCache.logRowsFetchFailure(error);
          throw error;
        }
      }

      this.mergeRows(rows, startMs, endMs);
      this.evict(windowStartTime.getTime());

      const stationIds: string[] = channels.map((channel: TChannel): string =>
        GuideCache.channelKey(channel)
      );
      return this.buildRows(stationIds, startMs, endMs);
    } finally {
      this.mInFlight.delete(key);
    }
  }

  /**
   * Rebuilds ordered guide rows for the given stations and window from cache.
   *
   * @param stationIds Stations in display order.
   * @param startMs Window start in epoch milliseconds.
   * @param endMs Window end in epoch milliseconds.
   * @returns Guide rows in station order.
   */
  private buildRows(
    stationIds: string[],
    startMs: number,
    endMs: number
  ): TGridGuideRow[] {
    const rows: TGridGuideRow[] = [];
    for (const stationId of stationIds) {
      rows.push({
        stationId,
        guideCells: this.getProgramsForRange(stationId, startMs, endMs)
      });
    }
    return rows;
  }

  /**
   * Merges a single row's cells into the station cache and records the range.
   *
   * @param row Guide row to merge.
   * @param startMs Fetched window start in epoch milliseconds.
   * @param endMs Fetched window end in epoch milliseconds.
   * @returns Nothing.
   */
  private mergeRow(row: TGridGuideRow, startMs: number, endMs: number): void {
    const existing: GuideRowData | undefined = this.mRows.get(row.stationId);
    const byKey: Map<string, TGuideCell> = new Map();

    if (existing !== undefined) {
      for (const cell of existing.programs) {
        byKey.set(GuideCache.cellKey(cell), cell);
      }
    }

    const incoming: TGuideCell[] =
      row.guideCells !== undefined && row.guideCells !== null
        ? row.guideCells
        : [];
    for (const cell of incoming) {
      byKey.set(GuideCache.cellKey(cell), cell);
    }

    const programs: TGuideCell[] = [...byKey.values()].sort(
      (a: TGuideCell, b: TGuideCell): number =>
        a.startTime.getTime() - b.startTime.getTime()
    );

    const existingFetched: GuideFetchedRange[] =
      existing === undefined ? [] : existing.fetchedRanges;
    const existingEmpty: GuideFetchedRange[] =
      existing === undefined ? [] : existing.emptyRanges;

    // A range only counts as "fetched" when the station contributed real
    // programs. A station that answered with none is recorded as an empty
    // range instead: it still suppresses re-requesting, but contributes no
    // cells, so the row renders as a placeholder and any program that arrives
    // later can never be displaced by it.
    const hasIncomingPrograms: boolean = incoming.length > 0;
    const fetchedRanges: GuideFetchedRange[] = hasIncomingPrograms
      ? GuideCache.mergeRange(existingFetched, { startMs, endMs })
      : existingFetched;
    const emptyRanges: GuideFetchedRange[] = hasIncomingPrograms
      ? existingEmpty
      : GuideCache.mergeRange(existingEmpty, { startMs, endMs });

    this.mRows.set(row.stationId, {
      stationId: row.stationId,
      programs,
      fetchedRanges,
      emptyRanges,
      earliestMs:
        programs.length > 0 ? programs[0].startTime.getTime() : startMs,
      latestMs:
        fetchedRanges.length > 0
          ? fetchedRanges[fetchedRanges.length - 1].endMs
          : endMs
    });
  }

  /**
   * Ranges that count as "already answered" for request planning.
   *
   * Policy: an empty answer suppresses re-requesting for the lifetime of the
   * cache instance (cleared by `clear()`), which bounds a genuinely empty
   * station to one request per session while still giving a transient empty
   * response another chance next session. Confined to this one method so the
   * policy can change (TTL, explicit refresh, never) without touching callers.
   *
   * @param rowData Cached row data for one station.
   * @returns Sorted, non-overlapping ranges the backend has answered.
   */
  private static answeredRanges(rowData: GuideRowData): GuideFetchedRange[] {
    if (rowData.emptyRanges.length === 0) {
      return rowData.fetchedRanges;
    }
    let combined: GuideFetchedRange[] = rowData.fetchedRanges;
    for (const range of rowData.emptyRanges) {
      combined = GuideCache.mergeRange(combined, range);
    }
    return combined;
  }

  /**
   * Returns whether one station fully covers [startMs, endMs) from its ranges.
   *
   * Coverage answers "must we request this?", so it spans both real-data and
   * empty answers. Content always comes from `programs` alone, so an empty
   * range never contributes cells.
   *
   * @param stationId Station to check.
   * @param startMs Window start in epoch milliseconds.
   * @param endMs Window end in epoch milliseconds.
   * @returns Whether the station fully covers the window.
   */
  private isStationCovered(
    stationId: string,
    startMs: number,
    endMs: number
  ): boolean {
    if (this.isJumpStation(stationId)) {
      return true;
    }

    const rowData: GuideRowData | undefined = this.mRows.get(stationId);
    if (rowData === undefined) {
      return false;
    }

    let cursorMs: number = startMs;
    for (const range of GuideCache.answeredRanges(rowData)) {
      if (range.startMs <= cursorMs && range.endMs > cursorMs) {
        cursorMs = range.endMs;
        if (cursorMs >= endMs) {
          return true;
        }
      }
    }
    return cursorMs >= endMs;
  }

  /**
   * Inserts a range into a sorted, non-overlapping range list, coalescing
   * adjacent/overlapping ranges.
   *
   * @param ranges Existing sorted ranges.
   * @param incoming Range to insert.
   * @returns A new, merged, sorted range list.
   */
  private static mergeRange(
    ranges: GuideFetchedRange[],
    incoming: GuideFetchedRange
  ): GuideFetchedRange[] {
    const all: GuideFetchedRange[] = [...ranges, incoming].sort(
      (a: GuideFetchedRange, b: GuideFetchedRange): number =>
        a.startMs - b.startMs
    );

    const merged: GuideFetchedRange[] = [];
    for (const range of all) {
      const last: GuideFetchedRange | undefined = merged[merged.length - 1];
      if (last !== undefined && range.startMs <= last.endMs) {
        last.endMs = Math.max(last.endMs, range.endMs);
      } else {
        merged.push({ startMs: range.startMs, endMs: range.endMs });
      }
    }
    return merged;
  }

  /**
   * Builds a stable de-duplication key for a program cell.
   *
   * [G8] Prefers `offerId` when present — it is the most stable and
   * unambiguous identifier for a specific offer/airing. Falls back to a
   * composite key built from start time, duration, contentId, and title
   * for cells that arrive without an offerId.
   *
   * @param cell Program cell.
   * @returns A deduplication key string.
   */
  private static cellKey(cell: TGuideCell): string {
    if (cell.offerId !== undefined && cell.offerId.length > 0) {
      return "offer|" + cell.offerId;
    }
    const contentId: string =
      cell.contentId !== undefined && cell.contentId !== null
        ? cell.contentId
        : "";
    const title: string =
      cell.title !== undefined && cell.title !== null ? cell.title : "";
    return (
      cell.startTime.getTime() +
      "|" +
      cell.duration +
      "|" +
      contentId +
      "|" +
      title
    );
  }

  // ─── Chunked prefetch (spec §3.1 / §3.2) ───────────────────────────────────

  /**
   * Warms the cache with the time buffer on each side of the visible window
   * (GUIDE_TIME_BUFFER_SLOTS slots), so subsequent left/right shifts are served
   * from memory. Already-covered sub-ranges are skipped. (Spec §1.1.)
   *
   * @param windowStartTime Visible window start time.
   * @param channels Channels to warm, in display order.
   * @returns Nothing.
   */
  public async prefetchTimeBuffer(
    windowStartTime: DateTime,
    channels: TChannel[]
  ): Promise<void> {
    if (channels.length === 0) {
      return;
    }
    const startMs: number = windowStartTime.getTime();
    const endMs: number =
      startMs + GUIDE_WINDOW_DURATION_MINUTES * MILLISECONDS_PER_MINUTE;
    const bufferMs: number = GUIDE_TIME_BUFFER_SLOTS * GUIDE_SHIFT_MS;

    await this.fetchRange(
      windowStartTime,
      channels,
      startMs - bufferMs,
      startMs
    );
    await this.fetchRange(windowStartTime, channels, endMs, endMs + bufferMs);
    this.evict(startMs);
  }

  /**
   * Extends the cached time range by GUIDE_PREFETCH_TIME_CHUNK_MINUTES beyond
   * the visible window edge in the requested direction. (Spec §3.1.)
   *
   * @param windowStartTime Visible window start time.
   * @param channels Channels to warm, in display order.
   * @param extendFuture True to extend forward; false to extend backward.
   * @returns Nothing.
   */
  public async prefetchTime(
    windowStartTime: DateTime,
    channels: TChannel[],
    extendFuture: boolean
  ): Promise<void> {
    if (channels.length === 0) {
      return;
    }
    const extent: GuideFetchedRange | null = this.computeCacheExtent();
    if (extent === null) {
      await this.prefetchTimeBuffer(windowStartTime, channels);
      return;
    }
    const cacheStartTime: number = extent.startMs;
    const cacheEndTime: number = extent.endMs;
    const chunkMs: number =
      GUIDE_PREFETCH_TIME_CHUNK_MINUTES * MILLISECONDS_PER_MINUTE;
    const visibleStartMs: number = windowStartTime.getTime();

    if (extendFuture) {
      await this.fetchRange(
        windowStartTime,
        channels,
        cacheEndTime,
        cacheEndTime + chunkMs
      );
    } else {
      await this.fetchRange(
        windowStartTime,
        channels,
        cacheStartTime - chunkMs,
        cacheStartTime
      );
    }
    this.evict(visibleStartMs);
  }

  /**
   * Fetches guide data for the given (typically newly scrolled-in) channels
   * across the full cached time extent, so a channel entering the window is
   * populated for every cached time slot — not just the visible buckets.
   * Falls back to the standard time buffer when the cache is empty. (Spec §3.2.)
   *
   * @param windowStartTime Reference window start time (offset anchor).
   * @param channels Channels whose data should be warmed.
   * @returns Nothing.
   */
  public async prefetchChannelData(
    windowStartTime: DateTime,
    channels: TChannel[]
  ): Promise<void> {
    if (channels.length === 0) {
      return;
    }
    const extent: GuideFetchedRange | null = this.computeCacheExtent();
    if (extent === null) {
      await this.prefetchTimeBuffer(windowStartTime, channels);
      return;
    }
    await this.fetchRange(
      windowStartTime,
      channels,
      extent.startMs,
      extent.endMs
    );
    this.evict(windowStartTime.getTime());
  }

  /**
   * Returns the number of 30-minute slots buffered beyond the visible window
   * edge in the requested direction. Zero when the cache is empty. (Spec §1.1.)
   *
   * @param windowStartTime Current visible window start time.
   * @param extendFuture True for the future direction; false for the past.
   * @returns Number of complete GUIDE_SHIFT_MS slots remaining before the cache edge.
   */
  public getRemainingTimeBufferSlots(
    windowStartTime: DateTime,
    extendFuture: boolean
  ): number {
    const extent: GuideFetchedRange | null = this.computeCacheExtent();
    if (extent === null) {
      return 0;
    }
    const visibleStartMs: number = windowStartTime.getTime();
    const visibleEndMs: number =
      visibleStartMs + GUIDE_WINDOW_DURATION_MINUTES * MILLISECONDS_PER_MINUTE;
    const remainingMs: number = extendFuture
      ? extent.endMs - visibleEndMs
      : visibleStartMs - extent.startMs;
    return Math.max(0, Math.floor(remainingMs / GUIDE_SHIFT_MS));
  }

  /**
   * Returns the number of station IDs (from the given list) that are already
   * fully covered across the current cache extent. Zero when the cache is
   * empty. (Spec §1.2.)
   *
   * @param stationIds Station identifiers to evaluate.
   * @returns Count of stations fully covered for [cacheStartMs, cacheEndMs).
   */
  public getRemainingChannelBuffer(stationIds: string[]): number {
    const extent: GuideFetchedRange | null = this.computeCacheExtent();
    if (extent === null) {
      return 0;
    }
    let coveredCount: number = 0;
    for (const stationId of stationIds) {
      if (this.isStationCovered(stationId, extent.startMs, extent.endMs)) {
        coveredCount++;
      }
    }
    return coveredCount;
  }

  /**
   * Fetches [startMs, endMs) for the given channels and merges the result,
   * splitting the request into ≤ GUIDE_MAX_FETCH_WINDOW_MINUTES time chunks and
   * ≤ GUIDE_MAX_STATIONS_PER_REQ station batches. Sub-ranges already fully
   * covered for every station in a batch are skipped. (Spec §3.1 / §3.2.)
   *
   * @param windowStartTime Reference time used to derive chunk offsets.
   * @param channels Channels to fetch, in display order.
   * @param startMs Range start in epoch milliseconds.
   * @param endMs Range end in epoch milliseconds.
   * @returns Nothing.
   */
  async fetchRange(
    windowStartTime: DateTime,
    channels: TChannel[],
    startMs: number,
    endMs: number
  ): Promise<void> {
    this.registerJumpChannels(channels);
    // Jump Channels have no linear schedule — never send them to the guide
    // rows service; their cells are synthesized locally instead.
    const fetchableChannels: TChannel[] = channels.filter(
      (channel: TChannel): boolean => channel.jumpChannelInfo === undefined
    );
    if (fetchableChannels.length === 0 || endMs <= startMs) {
      return;
    }

    const baseMs: number = windowStartTime.getTime();
    const maxChunkMs: number =
      GUIDE_MAX_FETCH_WINDOW_MINUTES * MILLISECONDS_PER_MINUTE;
    const batches: TChannel[][] = GuideCache.batchChannels(
      fetchableChannels,
      GUIDE_MAX_STATIONS_PER_REQ
    );

    const tasks: Promise<void>[] = [];
    for (
      let chunkStartMs: number = startMs;
      chunkStartMs < endMs;
      chunkStartMs += maxChunkMs
    ) {
      const chunkEndMs: number = Math.min(chunkStartMs + maxChunkMs, endMs);
      for (const batch of batches) {
        const batchStationIds: string[] = batch.map(
          (channel: TChannel): string =>
            channel.stationId !== undefined && channel.stationId !== null
              ? channel.stationId
              : ""
        );
        if (this.isWindowCovered(batchStationIds, chunkStartMs, chunkEndMs)) {
          continue;
        }
        tasks.push(
          this.fetchChunk(
            windowStartTime,
            baseMs,
            batch,
            chunkStartMs,
            chunkEndMs
          )
        );
      }
    }

    await Promise.all(tasks);
  }

  /**
   * Operational log for a failed guide rows API call. Every guide rows
   * request goes through this cache, so logging here covers all callers.
   * Cancelled (superseded) requests are not failures and are not logged.
   *
   * @param error Rejection from the guide rows service.
   */
  private static logRowsFetchFailure(error: unknown): void {
    if (isCancellation(error)) {
      return;
    }
    OperationalLogger.logUiError({
      errorCode: GuideErrorMapper.toCompositeCode(
        GUIDE_ERROR_CODE.ROWS_LOAD_FAILED,
        error
      ),
      errorMessage: GuideErrorMapper.toErrorMessage(
        error,
        "Failed to load guide rows"
      ),
      screen: GUIDE_SCREEN_NAME
    });
  }

  /**
   * Fetches and merges a single time chunk for one station batch.
   *
   * [G7] Checks the abort signal before and after the network call;
   * discards rows when the signal has been fired (the fallback in
   * fetchGuideRowsWithFallback would otherwise return dummy data).
   * [G9] Logs fetch duration for observability.
   * [G10] Sorts channels by stationId before the API call so the
   * request payload is consistent regardless of display order.
   *
   * @param windowStartTime Reference time used to derive chunk offsets.
   * @param baseMs Epoch milliseconds of windowStartTime.
   * @param channels Station batch to fetch.
   * @param chunkStartMs Chunk start in epoch milliseconds.
   * @param chunkEndMs Chunk end in epoch milliseconds.
   * @returns Nothing.
   */
  private async fetchChunk(
    windowStartTime: DateTime,
    baseMs: number,
    channels: TChannel[],
    chunkStartMs: number,
    chunkEndMs: number
  ): Promise<void> {
    // [G7] Snapshot the current abort signal before any await. If
    // abortAndReset() fires while the fetch is in flight it replaces
    // mFetchAbortController with a new (non-aborted) instance, so we must
    // capture a reference to the old signal here so the post-await check
    // correctly detects the abort on the *original* controller.
    const abortSignal: AbortSignal = this.mFetchAbortController.signal;

    // Bail out immediately if a newer load has already superseded this one.
    if (abortSignal.aborted) {
      Log.d(GuideCache.TAG, "fetchChunk — skipped (aborted before start)");
      return;
    }

    // [G10] Sort channels by stationId so the API request payload is
    // consistent regardless of the display order passed in.
    const sortedChannels: TChannel[] = [...channels].sort(
      (a: TChannel, b: TChannel): number => {
        const idA: string =
          a.stationId !== undefined && a.stationId !== null ? a.stationId : "";
        const idB: string =
          b.stationId !== undefined && b.stationId !== null ? b.stationId : "";
        if (idA < idB) {
          return -1;
        }
        if (idA > idB) {
          return 1;
        }
        return 0;
      }
    );

    const startOffsetMinutes: number =
      (chunkStartMs - baseMs) / MILLISECONDS_PER_MINUTE;
    const endOffsetMinutes: number =
      (chunkEndMs - baseMs) / MILLISECONDS_PER_MINUTE;

    // [G9] Capture start time for fetch duration logging.
    const fetchStartMs: number = Date.now();

    const interactor: ChannelsInteractor = new ChannelsInteractor();
    let rows: TGridGuideRow[];
    try {
      rows = await interactor.fetchGuideRowsWithFallback(
        {
          channels: sortedChannels,
          startTime: windowStartTime.addMinutes(startOffsetMinutes),
          endTime: windowStartTime.addMinutes(endOffsetMinutes)
        },
        { signal: abortSignal } // [G7]
      );
    } catch (error: unknown) {
      if (!abortSignal.aborted) {
        GuideCache.logRowsFetchFailure(error);
      }
      throw error;
    }

    // [G7] After the await, the abort signal may have fired while the request
    // was in flight; fetchGuideRowsWithFallback swallows AbortErrors and
    // returns dummy rows — discard them to avoid polluting the cache.
    if (abortSignal.aborted) {
      Log.d(GuideCache.TAG, "fetchChunk — discarding rows (aborted in-flight)");
      return;
    }

    // [G9] Log fetch duration for observability.
    Log.i(
      GuideCache.TAG,
      "fetchChunk() duration=" +
        String(Date.now() - fetchStartMs) +
        "ms stations=" +
        String(sortedChannels.length) +
        " chunkStart=" +
        String(chunkStartMs)
    );

    this.mergeRows(rows, chunkStartMs, chunkEndMs);
  }

  /**
   * Splits channels into batches of at most maxPerBatch.
   *
   * @param channels Channels to split.
   * @param maxPerBatch Maximum channels per batch.
   * @returns Array of channel batches.
   */
  private static batchChannels(
    channels: TChannel[],
    maxPerBatch: number
  ): TChannel[][] {
    const batches: TChannel[][] = [];
    for (let index: number = 0; index < channels.length; index += maxPerBatch) {
      batches.push(channels.slice(index, index + maxPerBatch));
    }
    return batches;
  }
}
